/**
 * One-Click Loan Eligibility Check — public multi-step page.
 *
 * URL flow:
 *   /eligibility                                        → step 1 (form + upload)
 *   → Stripe checkout (external)
 *   → /eligibility/result?check_id=…&session_id=…       → step 3 (verdict)
 *
 * ₹1,500 assessment fee. Rule-based backend analysis.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  IndianRupee,
  Loader2,
  ShieldCheck,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Eligibility } from "@/lib/api";
import UpiPaymentCard from "@/components/UpiPaymentCard";

const LOAN_TYPES = [
  { value: "personal_loan",         label: "Personal Loan",         basis: "monthly_income",  min: "₹10,000",  max: "₹2 Cr" },
  { value: "home_loan",             label: "Home Loan",             basis: "monthly_income",  min: "₹1 L",     max: "₹10 Cr" },
  { value: "business_loan",         label: "Business Loan",         basis: "annual_turnover", min: "₹50,000",  max: "₹5 Cr" },
  { value: "loan_against_property", label: "Loan Against Property", basis: "monthly_income",  min: "₹10,000",  max: "₹2 Cr" },
  { value: "vehicle_loan",          label: "Vehicle Loan",          basis: "monthly_income",  min: "₹25,000",  max: "₹50 L" },
];

const DOC_KINDS = [
  { value: "pan", label: "PAN Card", required: true },
  { value: "aadhaar", label: "Aadhaar Card", required: true },
  { value: "bank_statement", label: "Bank Statement (6 months)", required: true },
  { value: "salary_slips", label: "Salary Slips (last 3)", required: false },
  { value: "itr", label: "Income Tax Return (ITR)", required: false },
  { value: "supporting_1", label: "Supporting Document 1", required: false, optional_extra: true },
  { value: "supporting_2", label: "Supporting Document 2", required: false, optional_extra: true },
  { value: "supporting_3", label: "Supporting Document 3", required: false, optional_extra: true },
  { value: "supporting_4", label: "Supporting Document 4", required: false, optional_extra: true },
  { value: "other", label: "Other (optional)", required: false, optional_extra: true },
];
const REQUIRED_DOC_KINDS = DOC_KINDS.filter((d) => !d.optional_extra);
const EXTRA_DOC_KINDS = DOC_KINDS.filter((d) => d.optional_extra);

const MAX_FILE_MB = 5;
const MAX_FILES = 10;
const FEE = 1500;
const TERMS_VERSION = "2026-02-28";  // bump when the T&C copy below changes

const TERMS_ITEMS = [
  "The ₹1,500 eligibility check fee is strictly non-refundable, irrespective of the eligibility outcome (approved, partially approved, or ineligible).",
  "The eligibility verdict is a preliminary indicative assessment based on the information and documents you have provided. Actual loan approval, amount, tenure and interest rate are always subject to Sahyog Finance's full credit appraisal, KYC and lender partner underwriting.",
  "You confirm that every document uploaded is authentic, unaltered, self-attested, and belongs to you. Submitting forged, tampered or third-party documents may result in immediate rejection and legal action.",
  "You consent to Sahyog Finance & Noble Infinity Asset Management Pvt. Ltd. verifying, storing and processing the data you have shared (including with lender partners, credit bureaus and regulatory authorities) as per its Privacy Policy.",
  "Providing additional supporting documents (property papers, business proofs, extra bank statements, GST filings, salary confirmations, etc.) generally improves your loan eligibility. Sahyog Finance may request further clarifications during verification.",
  "Payment verification is manual and typically completes within 5 to 6 business days after UPI or bank-transfer proof is received. Your eligibility verdict will be emailed to your registered email as soon as verification concludes.",
  "You acknowledge that this eligibility check does not create any obligation on Sahyog Finance to sanction or disburse a loan, and any lending decision remains at the sole discretion of Sahyog Finance and its lender partners.",
  "This transaction is governed by Indian law with exclusive jurisdiction of the courts at Vyara, Tapi, Gujarat. Any dispute shall be resolved under the same jurisdiction.",
];

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

/* ------------------------------------------------------------------ shell */
function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04102B] via-[#08194a] to-[#020a1f] text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#04102B]/85 backdrop-blur">
        <div className="max-w-5xl mx-auto gutter min-h-16 py-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#F0D788] min-h-[44px]"
          >
            <ArrowLeft size={14} aria-hidden="true" /> Sahyog Finance &amp; Noble Infinity
          </Link>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
            Loan Eligibility Check
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

/* ================================================================
   Step 1 · Form + Uploads
   ================================================================ */
export default function EligibilityCheck() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    loan_type: "personal_loan",
    monthly_income: "",
    annual_turnover: "",
    employment_type: "salaried",
  });
  const [uploads, setUploads] = useState([]); // [{file, kind}]
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [paymentMethod] = useState("upi"); // UPI-only — Card/Net Banking removed Feb 2026
  const [upiStage, setUpiStage] = useState(null); // null | {check_id, submitted?, links?}
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsAcceptedAt, setTermsAcceptedAt] = useState(null);
  const [packLinks, setPackLinks] = useState(null); // { confirmation_url, document_pack_url, submitted_at }

  const loanCfg = LOAN_TYPES.find((l) => l.value === form.loan_type) || LOAN_TYPES[0];
  const needsIncome = loanCfg.basis === "monthly_income";

  const addFile = (kind) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`${f.name} exceeds the ${MAX_FILE_MB} MB limit.`);
      e.target.value = "";
      return;
    }
    if (uploads.length >= MAX_FILES) {
      setError(`You can upload at most ${MAX_FILES} documents.`);
      e.target.value = "";
      return;
    }
    setUploads((u) => [
      ...u.filter((x) => x.kind !== kind),
      { file: f, kind, id: `${kind}-${Date.now()}` },
    ]);
    setError("");
    e.target.value = "";
  };

  const removeFile = (id) => setUploads((u) => u.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (needsIncome && (!form.monthly_income || Number(form.monthly_income) <= 0)) {
      setError("Please enter your monthly income to compute eligibility.");
      return;
    }
    if (!needsIncome && (!form.annual_turnover || Number(form.annual_turnover) <= 0)) {
      setError("Please enter your annual turnover to compute eligibility.");
      return;
    }
    if (!termsAccepted) {
      setError("Please read and accept the Terms & Conditions before continuing.");
      return;
    }
    setBusy(true);
    try {
      const files = uploads.map((u) => u.file);
      const kinds = uploads.map((u) => u.kind);
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        loan_type: form.loan_type,
        employment_type: form.employment_type,
        monthly_income: needsIncome ? Number(form.monthly_income) : 0,
        annual_turnover: !needsIncome ? Number(form.annual_turnover) : 0,
        terms_accepted: true,
        terms_accepted_at: termsAcceptedAt || new Date().toISOString(),
        terms_version: TERMS_VERSION,
      };
      const applyRes = await Eligibility.apply(payload, files, kinds);
      if (paymentMethod === "upi") {
        // Show the UPI card in-page. On upload we call ManualPayments.submit
        // and then show a success card. No Stripe redirect.
        setUpiStage({ check_id: applyRes.check_id });
        setBusy(false);
        return;
      }
      const originUrl = window.location.origin;
      const checkoutRes = await Eligibility.checkout(applyRes.check_id, originUrl);
      window.location.href = checkoutRes.url;
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Something went wrong. Please try again.",
      );
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <main id="main-content" tabIndex={-1} className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14 space-y-8">
        <section>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
            One-Click Assessment
          </div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl leading-tight">
            Check Your Loan Eligibility
          </h1>
          <p className="mt-3 text-white/70 leading-relaxed max-w-2xl">
            Fill in the details below, upload your documents, and pay a one-time assessment
            fee of{" "}
            <span className="text-[#F0D788] font-semibold">₹{inr(FEE)}</span>. You&apos;ll get
            your pre-eligible loan amount and deposit requirement within 5 to 6 working days — emailed to you
            and shown on-screen.
          </p>

          {/* Disclaimer strip — high-prominence per Feb 2026 request */}
          <div
            data-testid="el-disclaimer"
            className="mt-5 rounded-2xl border-2 border-[#D4AF37]/50 bg-[#D4AF37]/[0.08] px-5 py-4 flex items-start gap-3"
            role="note"
          >
            <AlertCircle size={20} className="text-[#F0D788] mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <div className="text-sm text-white font-semibold">
                This is only an eligibility check.
              </div>
              <div className="text-xs md:text-sm text-white/80 mt-1 leading-relaxed">
                It gives you an <strong>indicative</strong> loan amount &amp; deposit
                requirement. It <strong>does not guarantee loan approval</strong> and{" "}
                <strong>is not a sanction letter</strong>. Final approval is subject to full
                credit appraisal, KYC verification and Sahyog Finance underwriting policy.
              </div>
            </div>
          </div>

          {/* Loan amount ranges per product */}
          <div
            data-testid="el-loan-ranges"
            className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4"
          >
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#F0D788] font-semibold">
              Eligible loan amount ranges
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2.5">
              {LOAN_TYPES.map((l) => (
                <div
                  key={l.value}
                  data-testid={`el-range-${l.value}`}
                  className="rounded-xl border border-[#D4AF37]/20 bg-black/25 px-3 py-2.5"
                >
                  <div className="text-[10px] uppercase tracking-wider text-white/55">
                    {l.label}
                  </div>
                  <div className="mt-1 text-sm text-white font-semibold tabular-nums">
                    {l.min} <span className="text-white/40">—</span> {l.max}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <form
          onSubmit={submit}
          data-testid="eligibility-form"
          className="rounded-3xl bg-white/[0.04] border border-[#D4AF37]/25 p-6 md:p-10 space-y-6"
        >
          {/* Personal details */}
          <fieldset>
            <legend className="text-[11px] uppercase tracking-[0.25em] text-[#F0D788] mb-4">
              1 · Your details
            </legend>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input
                  data-testid="el-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={inputCls}
                  placeholder="Ravi Patel"
                />
              </Field>
              <Field label="Mobile number" required>
                <input
                  data-testid="el-phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className={inputCls}
                  placeholder="10-digit mobile"
                />
              </Field>
              <Field label="Email" required>
                <input
                  data-testid="el-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className={inputCls}
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Employment type">
                <select
                  data-testid="el-employment"
                  value={form.employment_type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, employment_type: e.target.value }))
                  }
                  className={inputCls}
                >
                  <option value="salaried">Salaried</option>
                  <option value="self_employed">Self-employed</option>
                  <option value="business">Business owner</option>
                  <option value="retired">Retired / Pensioner</option>
                </select>
              </Field>
            </div>
          </fieldset>

          {/* Loan type + income */}
          <fieldset>
            <legend className="text-[11px] uppercase tracking-[0.25em] text-[#F0D788] mb-4">
              2 · Loan &amp; income
            </legend>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Loan type" required>
                <select
                  data-testid="el-loan-type"
                  value={form.loan_type}
                  onChange={(e) => setForm((f) => ({ ...f, loan_type: e.target.value }))}
                  className={inputCls}
                >
                  {LOAN_TYPES.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </Field>
              {needsIncome ? (
                <Field label="Monthly income (₹)" required>
                  <IncomeInput
                    testId="el-monthly-income"
                    value={form.monthly_income}
                    onChange={(v) => setForm((f) => ({ ...f, monthly_income: v }))}
                    placeholder="50000"
                  />
                </Field>
              ) : (
                <Field label="Annual turnover (₹)" required>
                  <IncomeInput
                    testId="el-annual-turnover"
                    value={form.annual_turnover}
                    onChange={(v) => setForm((f) => ({ ...f, annual_turnover: v }))}
                    placeholder="5000000"
                  />
                </Field>
              )}
            </div>
          </fieldset>

          {/* Uploads */}
          <fieldset>
            <legend className="text-[11px] uppercase tracking-[0.25em] text-[#F0D788] mb-4">
              3 · Documents (max {MAX_FILE_MB} MB each · PDF / JPG / PNG)
            </legend>
            <p className="text-xs text-white/55 mb-4">
              Upload{" "}
              <strong className="text-white/80">PAN, Aadhaar, Bank Statement, and either an ITR or Salary Slip</strong>{" "}
              to qualify for a no-deposit assessment. Missing documents will trigger a 30%
              deposit recommendation.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {REQUIRED_DOC_KINDS.map((d) => {
                const attached = uploads.find((u) => u.kind === d.value);
                return (
                  <label
                    key={d.value}
                    data-testid={`el-doc-${d.value}`}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition ${
                      attached
                        ? "bg-emerald-500/[0.06] border-emerald-500/40"
                        : "bg-white/[0.03] border-white/10 hover:border-[#D4AF37]/40"
                    }`}
                  >
                    {attached ? (
                      <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                    ) : (
                      <Upload size={16} className="text-[#F0D788] shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white/90 flex items-center gap-1.5">
                        {d.label}
                        {d.required && <span className="text-[#D4AF37]">*</span>}
                      </div>
                      {attached ? (
                        <div className="text-[11px] text-white/55 truncate">
                          {attached.file.name} · {(attached.file.size / 1024).toFixed(0)} KB
                        </div>
                      ) : (
                        <div className="text-[11px] text-white/40">Click to upload</div>
                      )}
                    </div>
                    {attached && (
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.preventDefault();
                          removeFile(attached.id);
                        }}
                        className="p-1.5 text-white/60 hover:text-rose-300"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,image/jpeg,image/jpg,image/png,image/webp"
                      onChange={addFile(d.value)}
                    />
                  </label>
                );
              })}
            </div>

            {/* Optional supporting documents */}
            <div className="mt-6 mb-3 flex items-center gap-2">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-[10px] uppercase tracking-[0.24em] text-white/45">
                Optional supporting documents
              </span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {EXTRA_DOC_KINDS.map((d) => {
                const attached = uploads.find((u) => u.kind === d.value);
                return (
                  <label
                    key={d.value}
                    data-testid={`el-doc-${d.value}`}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition ${
                      attached
                        ? "bg-emerald-500/[0.06] border-emerald-500/40"
                        : "bg-white/[0.02] border-white/10 hover:border-[#D4AF37]/40 border-dashed"
                    }`}
                  >
                    {attached ? (
                      <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                    ) : (
                      <Upload size={16} className="text-white/50 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white/85">{d.label}</div>
                      {attached ? (
                        <div className="text-[11px] text-white/55 truncate">
                          {attached.file.name} · {(attached.file.size / 1024).toFixed(0)} KB
                        </div>
                      ) : (
                        <div className="text-[11px] text-white/40">Optional · click to upload</div>
                      )}
                    </div>
                    {attached && (
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.preventDefault();
                          removeFile(attached.id);
                        }}
                        className="p-1.5 text-white/60 hover:text-rose-300"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,image/jpeg,image/jpg,image/png,image/webp"
                      onChange={addFile(d.value)}
                    />
                  </label>
                );
              })}
            </div>
          </fieldset>

          {/* Payment method — UPI only */}
          <fieldset>
            <legend className="text-[11px] uppercase tracking-[0.25em] text-[#F0D788] mb-4">
              4 · Payment method
            </legend>
            <div
              data-testid="el-payment-upi-only"
              className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] px-5 py-4 flex items-start gap-3"
            >
              <ShieldCheck size={18} className="text-[#F0D788] mt-0.5 shrink-0" />
              <div>
                <div className="text-sm text-[#F0D788] font-semibold">
                  Pay by UPI · scan the QR or use our UPI ID
                </div>
                <div className="text-[11px] text-white/60 mt-1 leading-relaxed">
                  Our team confirms your payment manually within 5 to 6 business days. Card and
                  Net Banking are not accepted for eligibility assessments.
                </div>
              </div>
            </div>
          </fieldset>

          {/* Step 5 · Terms & Conditions — the customer must acknowledge before paying */}
          <fieldset data-testid="el-terms-fieldset">
            <legend className="text-[11px] uppercase tracking-[0.25em] text-[#F0D788] mb-4">
              5 · Terms &amp; Conditions
            </legend>
            <div className="rounded-2xl border border-[#D4AF37]/35 bg-[#0A1F44] p-5">
              <div
                data-testid="el-terms-scroll"
                className="max-h-64 overflow-y-auto pr-2 space-y-3 text-[13px] leading-relaxed text-white/80"
              >
                {TERMS_ITEMS.map((t, idx) => (
                  <div key={`term-${idx}-${t.slice(0, 24)}`} className="flex gap-2.5">
                    <span className="text-[#D4AF37] font-bold shrink-0">{String(idx + 1).padStart(2, "0")}.</span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
              <label
                data-testid="el-terms-accept-label"
                className={`mt-5 flex items-start gap-3 rounded-xl border-2 px-4 py-3.5 cursor-pointer transition ${
                  termsAccepted
                    ? "bg-emerald-500/[0.08] border-emerald-500/50"
                    : "bg-white/[0.03] border-[#D4AF37]/40 hover:border-[#D4AF37]/70"
                }`}
              >
                <input
                  type="checkbox"
                  data-testid="el-terms-accept"
                  checked={termsAccepted}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setTermsAccepted(checked);
                    setTermsAcceptedAt(checked ? new Date().toISOString() : null);
                    if (checked) setError("");
                  }}
                  className="mt-0.5 w-5 h-5 rounded accent-[#D4AF37] cursor-pointer shrink-0"
                />
                <div className="text-sm text-white/90 leading-relaxed">
                  <b>I have read, understood and accept the above Terms &amp; Conditions.</b>{" "}
                  I acknowledge that the ₹{inr(FEE)} eligibility check fee is <b className="text-rose-200">non-refundable</b>,
                  and I authorise Sahyog Finance &amp; Noble Infinity Asset Management Pvt. Ltd. to
                  process my application on this basis.
                </div>
              </label>
              <div className="mt-2 text-[11px] text-white/45">
                Terms version {TERMS_VERSION}. A copy of these Terms will be printed on your Confirmation and Document Pack PDFs.
              </div>
            </div>
          </fieldset>

          {error && (
            <div
              data-testid="el-error"
              className="flex items-start gap-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm px-3 py-2.5"
            >
              <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}

          {/* Fee summary */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] p-5">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#F0D788]">
                Assessment fee
              </div>
              <div className="font-serif text-3xl mt-1">₹{inr(FEE)}</div>
              <div className="text-[11px] text-white/60 mt-0.5">
                One-time, inclusive of GST · <span className="text-rose-200 font-semibold">non-refundable</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={busy || !termsAccepted}
              data-testid="el-submit"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full btn-gold text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <Loader2 size={15} className="animate-spin" /> Processing…
                </>
              ) : paymentMethod === "upi" ? (
                <>
                  Continue to UPI payment <ArrowRight size={14} />
                </>
              ) : (
                <>
                  Pay ₹{inr(FEE)} &amp; Check <ArrowRight size={14} />
                </>
              )}
            </button>
          </div>

          <div className="text-[11px] text-white/40 flex items-start gap-1.5 leading-relaxed">
            <ShieldCheck size={11} className="mt-0.5 text-[#D4AF37] shrink-0" />
            <span>
              Your data is encrypted in transit and at rest. This is an indicative
              pre-eligibility assessment — final loan approval is subject to full credit
              appraisal, KYC verification and internal underwriting policy of Sahyog
              Finance &amp; Noble Infinity Asset Management Pvt. Ltd.
            </span>
          </div>
        </form>

        {/* UPI stage — shown after form submit when UPI method is selected */}
        {upiStage && !upiStage.submitted && (
          <div data-testid="el-upi-stage" ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "start" })}>
            <UpiPaymentCard
              kind="eligibility"
              targetId={upiStage.check_id}
              amount={FEE}
              payerName={form.name}
              payerEmail={form.email}
              payerPhone={form.phone}
              testIdPrefix="el-upi"
              onSubmitted={async (res) => {
                setUpiStage((s) => ({ ...s, submitted: true, payment_id: res.payment_id }));
                try {
                  const backend = process.env.REACT_APP_BACKEND_URL || "";
                  const r = await fetch(`${backend}/api/eligibility/${upiStage.check_id}/pack-links`);
                  if (r.ok) setPackLinks(await r.json());
                } catch (_e) {
                  // Pack-links prefetch is non-blocking — the payment flow continues either way.
                  if (process.env.NODE_ENV === "development") console.debug("pack-links prefetch failed:", _e);
                }
              }}
            />
          </div>
        )}

        {upiStage && upiStage.submitted && (
          <section
            data-testid="el-upi-pending"
            ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="rounded-3xl border border-emerald-500/40 bg-emerald-500/[0.06] p-8 md:p-10 text-center"
          >
            <CheckCircle2 size={40} className="mx-auto text-emerald-400" aria-hidden="true" />
            <h2 className="mt-6 font-serif text-2xl md:text-3xl text-white">
              Payment proof submitted!
            </h2>
            <p className="mt-3 text-white/70 leading-relaxed max-w-xl mx-auto">
              Your check status is now{" "}
              <strong className="text-emerald-300">Pending Verification</strong>. Our team
              will confirm your UPI/Bank transfer within 5 to 6 business days. You&apos;ll receive
              the eligibility verdict at{" "}
              <strong className="text-white">{form.email}</strong> once approved.
            </p>
            <div className="mt-6 inline-block rounded-xl bg-black/40 border border-[#D4AF37]/40 px-6 py-4 text-left">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                Your reference ID
              </div>
              <div
                data-testid="el-upi-reference"
                className="font-mono text-2xl md:text-3xl text-[#F0D788] mt-1 tracking-wider"
              >
                {(upiStage.check_id || "").slice(0, 8).toUpperCase()}
              </div>
              <div className="text-[10px] text-white/50 mt-1.5">
                Save this ID — quote it in any follow-up
              </div>
              <div className="mt-4 pt-3 border-t border-white/10 text-[11px] text-white/70 space-y-1">
                <div><span className="text-white/45">Submitted:</span>{" "}
                  <span data-testid="el-upi-submitted-at">{new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })} IST</span>
                </div>
                <div><span className="text-white/45">Email:</span>{" "}<span>{form.email}</span></div>
                <div><span className="text-white/45">Status:</span>{" "}<span className="text-emerald-300">Pending Verification</span></div>
              </div>
            </div>
            {/* SLA countdown chip (5-6 business days) */}
            <SlaCountdownChip submittedAt={packLinks?.submitted_at || null} />
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                data-testid="el-download-pack"
                href={packLinks?.document_pack_url || `${process.env.REACT_APP_BACKEND_URL || ""}/api/eligibility/${upiStage.check_id}/document-pack.pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-[#0A1F44] hover:brightness-110 text-sm font-semibold shadow-lg shadow-[#D4AF37]/20 min-h-[44px]"
              >
                <Download size={14} /> Download Eligibility Pack (PDF)
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-outline-gold text-sm min-h-[44px]"
              >
                Back to home
              </Link>
            </div>
            <p className="mt-5 text-[10px] text-white/40 max-w-md mx-auto leading-relaxed">
              The Eligibility Pack is a single PDF containing your application, uploaded documents,
              accepted Terms &amp; Conditions and this confirmation. Only you (with this Reference
              ID) and our admin team can download it.
            </p>
          </section>
        )}
      </main>
    </PageShell>
  );
}

/* -------------------- MethodPill (payment method chooser) -------------------- */
function MethodPill({ testId, selected, onClick, title, sub, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-4 rounded-2xl border transition ${
        selected
          ? "border-[#D4AF37]/70 bg-[#D4AF37]/[0.08] shadow-inner"
          : "border-white/10 bg-white/[0.03] hover:border-[#D4AF37]/40"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm text-white font-semibold">{title}</div>
        {badge && (
          <span className="text-[10px] uppercase tracking-wider text-[#F0D788] border border-[#D4AF37]/40 rounded-full px-2 py-0.5">
            {badge}
          </span>
        )}
      </div>
      <div className="mt-1.5 text-xs text-white/55 leading-snug">{sub}</div>
      {selected && (
        <div className="mt-2 text-[11px] text-[#F0D788] flex items-center gap-1">
          <CheckCircle2 size={11} /> Selected
        </div>
      )}
    </button>
  );
}

/* ================================================================
   Step 3 · Result (auto-polls after Stripe redirect)
   ================================================================ */
export function EligibilityResult() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const checkId = params.get("check_id");
  const [state, setState] = useState({ status: "polling", result: null, error: "", checkId: null });
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    try {
      if (sessionId) {
        const r = await Eligibility.status(sessionId);
        if (r.payment_status === "paid" && r.result) {
          setState({ status: "ready", result: r.result, error: "", checkId: r.check_id || checkId });
          return;
        }
        if (r.payment_status === "unpaid" || r.payment_status === "canceled") {
          setState({
            status: "unpaid",
            result: null,
            error: "Payment was not completed.",
            checkId: r.check_id || checkId,
          });
          return;
        }
      } else if (checkId) {
        const r = await Eligibility.result(checkId);
        setState({ status: "ready", result: r.result, error: "", checkId });
        return;
      } else {
        setState({ status: "error", result: null, error: "Missing session or check id.", checkId: null });
        return;
      }
    } catch (err) {
      if (attempts > 15) {
        setState({
          status: "error",
          result: null,
          error:
            "Could not confirm your payment after several attempts. Please contact us at info.nobleinfinity@gmail.com with your Stripe receipt.",
          checkId,
        });
        return;
      }
    }
    setAttempts((a) => a + 1);
  }, [sessionId, checkId, attempts]);

  useEffect(() => {
    if (state.status !== "polling") return;
    poll();
    const t = setTimeout(poll, 2500);
    return () => clearTimeout(t);
  }, [poll, state.status]);

  const goDone = () => nav("/");

  return (
    <PageShell>
      <main id="main-content" tabIndex={-1} className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20 space-y-8">
        {state.status === "polling" && (
          <div
            data-testid="el-polling"
            className="rounded-3xl bg-white/[0.04] border border-[#D4AF37]/25 p-10 text-center"
          >
            <Loader2 size={32} className="mx-auto text-[#F0D788] animate-spin" />
            <h2 className="mt-6 font-serif text-2xl">Confirming your payment…</h2>
            <p className="mt-3 text-white/60 text-sm">
              Please don&apos;t close this page. This usually takes 5-15 seconds.
            </p>
          </div>
        )}

        {state.status === "ready" && state.result && (
          <ResultCard result={state.result} checkId={state.checkId || checkId} onDone={goDone} />
        )}

        {(state.status === "unpaid" || state.status === "error") && (
          <div
            data-testid="el-error-card"
            className="rounded-3xl bg-rose-500/10 border border-rose-500/30 p-10 text-center"
          >
            <AlertCircle size={32} className="mx-auto text-rose-300" />
            <h2 className="mt-6 font-serif text-2xl">
              {state.status === "unpaid" ? "Payment not completed" : "Something went wrong"}
            </h2>
            <p className="mt-3 text-rose-100 text-sm">{state.error}</p>
            <Link
              to="/eligibility"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold font-semibold text-sm"
            >
              Start a new check
            </Link>
          </div>
        )}
      </main>
    </PageShell>
  );
}

/* -------------------- ResultCard -------------------- */
function ResultCard({ result, checkId, onDone }) {
  const amount = result?.eligible_amount || 0;
  const depositOk = !result?.deposit_required;
  const packUrl = checkId
    ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/eligibility/${checkId}/document-pack.pdf`
    : null;
  return (
    <section
      data-testid="el-result-card"
      className="rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#0e2a6b] via-[#1c3d92] to-[#0A1F44] p-8 md:p-12"
    >
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#F0D788]">
        Your assessment
      </div>
      <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl">{result.loan_type_label}</h2>
          <div className="mt-1 text-white/70 text-sm">Indicative pre-eligible amount</div>
        </div>
        <div
          data-testid="el-result-amount"
          className="font-serif text-4xl md:text-5xl text-[#F0D788]"
        >
          ₹{inr(amount)}
        </div>
      </div>

      <div
        data-testid="el-result-deposit"
        className={`mt-8 rounded-2xl border px-5 py-4 flex items-center gap-3 ${
          depositOk
            ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-100"
            : "bg-amber-500/10 border-amber-500/40 text-amber-100"
        }`}
      >
        {depositOk ? (
          <CheckCircle2 size={22} className="shrink-0" />
        ) : (
          <Building2 size={22} className="shrink-0" />
        )}
        <div>
          <div className="text-sm font-semibold">{result.deposit_reason}</div>
          <div className="text-[11px] opacity-80 mt-0.5">
            {depositOk
              ? "Straight-through processing on standard T&C."
              : "A 30% security deposit will be required at loan disbursal."}
          </div>
        </div>
      </div>

      <div className="mt-6 text-sm text-white/85 leading-relaxed">
        <div className="text-[11px] uppercase tracking-wider text-[#F0D788] mb-1.5">
          Assessment note
        </div>
        {result.reason}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
        {result.basis && (
          <div className="rounded-xl border border-white/10 bg-black/20 p-3">
            <div className="text-[10px] uppercase text-white/40">Basis</div>
            <div className="mt-1 text-white/85">
              {result.multiplier}× {result.basis === "monthly_income" ? "monthly income" : "annual turnover"}
            </div>
          </div>
        )}
        <div className="rounded-xl border border-white/10 bg-black/20 p-3">
          <div className="text-[10px] uppercase text-white/40">Documents</div>
          <div className="mt-1 text-white/85">
            {result.sufficient_docs ? "Complete set uploaded" : "Partial set"}
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3 pt-6 border-t border-white/10">
        <button
          type="button"
          onClick={onDone}
          data-testid="el-result-done"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold"
        >
          Done
        </button>
        {packUrl && (
          <a
            href={packUrl}
            target="_blank"
            rel="noreferrer"
            download
            data-testid="el-result-download-pack"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/[0.08] hover:bg-[#D4AF37]/[0.15] text-sm text-[#F0D788] font-medium transition"
          >
            <Download size={13} /> Download Document Pack (PDF)
          </a>
        )}
        <Link
          to="/apply/loan"
          data-testid="el-result-apply"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-outline-gold text-sm"
        >
          Start formal loan application <ArrowRight size={13} />
        </Link>
      </div>

      <p className="mt-6 text-[11px] text-white/55 leading-relaxed">
        <strong className="text-white/70">Disclaimer:</strong> This is an indicative
        pre-eligibility assessment based on the details and documents you provided. Final loan
        approval, amount and terms are subject to full credit appraisal, KYC verification and
        internal underwriting policies of Sahyog Finance &amp; Noble Infinity Asset Management
        Pvt. Ltd. Nothing on this page constitutes a binding commitment to lend.
      </p>
    </section>
  );
}

/* -------------------- small helpers -------------------- */
const inputCls =
  "w-full px-4 py-2.5 rounded-lg bg-white/[0.05] border border-[#D4AF37]/20 outline-none focus:border-[#D4AF37]/60 text-sm text-white placeholder:text-white/30";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-white/55 mb-1.5">
        {label}
        {required && <span className="text-[#D4AF37]"> *</span>}
      </span>
      {children}
    </label>
  );
}

function IncomeInput({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative">
      <IndianRupee
        size={13}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
      />
      <input
        data-testid={testId}
        type="number"
        min="0"
        step="100"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls} pl-8`}
        placeholder={placeholder}
        required
      />
    </div>
  );
}


/* -------------------- SLA Countdown Chip -------------------- */
function _businessDaysBetween(fromDate, toDate) {
  // Count Mon-Fri days (inclusive of `fromDate`, exclusive of `toDate`) with IST timezone.
  const ONE_DAY = 24 * 60 * 60 * 1000;
  let count = 0;
  const from = new Date(fromDate);
  const to = new Date(toDate);
  // Normalise to date-only (drop time)
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  const d = new Date(from);
  while (d < to) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) count += 1;
    d.setTime(d.getTime() + ONE_DAY);
  }
  return count;
}

function SlaCountdownChip({ submittedAt }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000); // refresh every minute
    return () => clearInterval(id);
  }, []);
  if (!submittedAt) return null;
  const dayElapsed = Math.max(0, _businessDaysBetween(submittedAt, now));
  const current = Math.min(6, dayElapsed + 1); // Day 1 on submission day, Day 6 at end
  const state =
    current <= 3 ? "on-track" :
    current <= 5 ? "midway" :
    current <= 6 ? "final" : "overdue";
  const toneClass = {
    "on-track": "border-emerald-400/50 bg-emerald-500/[0.08] text-emerald-200",
    "midway":   "border-amber-400/50  bg-amber-500/[0.08]  text-amber-200",
    "final":    "border-orange-400/50 bg-orange-500/[0.08] text-orange-200",
    "overdue":  "border-rose-400/50   bg-rose-500/[0.08]   text-rose-200",
  }[state];
  const label = state === "overdue"
    ? `Verifying · Overdue (Day ${dayElapsed + 1})`
    : `Verifying · Day ${current} of 6`;
  return (
    <div
      data-testid="el-sla-chip"
      className={`mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-semibold tracking-wider uppercase ${toneClass}`}
    >
      <Clock size={13} />
      {label}
    </div>
  );
}

